import { useMemo } from 'react';
import { List, Box, Typography, Grid } from '@mui/material';
import { useFlags } from 'hooks/useFeatureFlag';
import { useLocation } from 'react-router-dom';
import { Link } from 'components/Link';
import { Routes } from 'fnRoutes';
import { MAIN_COLOR } from 'theme/constant';
import { Business } from 'models/business';
import { useAccountBusiness } from 'auth/AccountBusinessProvider';
import smallLogo from 'components/common/Icon/assets/formationsLogo.png';
import logoFull from 'icons/formationsLogo/formationslogo-full-black-large.png';
import { IAccount } from 'models/account';
import { useAccount, useNavigate, useSpoof, useUser } from 'hooks/api';
import { useCompany, useCurrentCompanyList } from 'hooks/api/useCompanies';
import * as yup from 'yup';
import { useCurrentUserFeatureList } from 'hooks/api/useFeatures';
import { generateAdminBusinessActions, IAction } from '../constant';
import { filterVisibleActions } from '../helpers';
import { AccountMenuItem } from './AccountMenuItem';
import { useSidebarOpen } from './sidebarContext';
import { replaceBusinessId } from './helpers';
import { BusinessSelector } from './BusinessSelector';

export const createBusinessSchema = yup.object().shape({
  industryCode: yup.string().required('Please select an industry.'),
  entityType: yup.string().required('Please select an entity type.'),
  formationsPlan: yup.string().required('Please select a plan.'),
});

interface Props {
  actions: IAction[];
}

export const isOwner = (
  business: Business,
  accounts: IAccount[],
  userId?: string,
) => {
  if (!userId) {
    return false;
  }
  const account = accounts.find((a) => business.accountId === a.id);
  return account?.ownerUserId === userId;
};

export const AccountDetailHeader = ({ actions }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { accountId, businessId, setBusinessId } = useAccountBusiness();
  const { account } = useAccount(accountId || undefined);
  const { user } = useUser(account?.ownerUserId);
  const { company } = useCompany(businessId || businessId || undefined);
  const { isAdminSpoof } = useSpoof();
  const home = isAdminSpoof ? Routes.CUSTOMER_HOME : Routes.HOME();
  const { companies: userBusinesses } = useCurrentCompanyList(
    account?.ownerUserId,
  );

  // feature flags
  const flags = useFlags();
  const featureList = useCurrentUserFeatureList();
  const enabledActions = filterVisibleActions(actions, flags, featureList);
  const open = useSidebarOpen();
  const handleSwitchCompany = (newBusinessId: string) => {
    setBusinessId(newBusinessId);
    const newPath = replaceBusinessId(location.pathname, newBusinessId);
    navigate(newPath);
  };

  const businessActions = useMemo(() => {
    if (!accountId || !businessId) {
      return [];
    }
    return generateAdminBusinessActions(accountId, businessId);
  }, [accountId, businessId]);

  const backAction = enabledActions.slice(0, 1);
  const otherActions = enabledActions.slice(1);

  const fullName = user?.name ? `${user?.name?.first} ${user?.name?.last}` : '';

  return (
    <Box>
      <Box p={4} mb={4} sx={{ opacity: open ? 1 : 0 }}>
        <Box data-testid="logo">
          <Link to={home}>
            <img
              src={logoFull}
              alt="formations"
              width="168px"
              color={MAIN_COLOR}
            />
          </Link>
        </Box>
      </Box>
      <List>
        {backAction.map((action) => (
          <AccountMenuItem
            key={action.id}
            {...action}
            active={action.id === 'welcome'}
          />
        ))}
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body1B">
            {user?.preferredName ?? fullName}
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant="body3S"
            color="text.secondary"
            width="100%"
            pl={4}
          >
            {userBusinesses.length} BUSINESSES
          </Typography>
          <BusinessSelector
            userId={account?.ownerUserId || ''}
            accountId={accountId || ''}
            businessId={businessId || ''}
            handleSwitchCompany={handleSwitchCompany}
          />
        </Box>
        <Box
          sx={{
            mt: 4,
            pl: 2,
          }}
        >
          <Typography variant="body1S" sx={{}}>
            YOUR ACCOUNT
          </Typography>
        </Box>
        {otherActions.map((action) => (
          <AccountMenuItem
            key={action.id}
            {...action}
            active={action.id === 'welcome'}
          />
        ))}
        <Box
          sx={{
            mt: 4,
            pl: 2,
          }}
        >
          <Typography variant="body1S">YOUR BUSINESS</Typography>
          <Grid
            container
            display="flex"
            alignItems="flex-start"
            flexDirection="row"
            width="168px"
            sx={{
              mt: 2,
            }}
          >
            <img
              src={smallLogo}
              alt={company?.name}
              width="24px"
              height="24px"
              style={{ marginRight: '8px' }}
            />
            <Grid item width="136px" flexDirection="column">
              <Typography
                variant="body2B"
                component="p"
                width="136px"
                maxWidth="136px"
                overflow="hidden"
                textOverflow="ellipsis"
                marginBottom="4px"
              >
                {company?.name || company?.id}
              </Typography>
              <Typography
                variant="body3S"
                color="text.secondary"
                component="p"
                width="136px"
                maxWidth="136px"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {company?.accountId === accountId ? 'Owner' : 'Member'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        {businessActions.map((action) => (
          <AccountMenuItem
            key={action.id}
            {...action}
            active={action.id === 'welcome'}
          />
        ))}
      </List>
    </Box>
  );
};
